import { Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { EFileEntity } from '@shs-digi-care-monorepo/interfaces/enums';
import {
    EMPTY_PATH_PREFIX,
    IDeleteRequestFileInput,
    IDownloadRequestAttachmentInput,
    IListFileInput,
    IS3File
} from '@shs-digi-care-monorepo/interfaces';
import { IUploadFileInput } from '../../common/shared/interfaces/IUploadFileInput';
import { UploadFile } from '../../common/shared/interfaces/uploadFile';
import { environment } from '../../../environments/environment';
import { AbstractAttachmentService } from '../../common/shared/services/abstract-attachment.service';

@Injectable({
    providedIn: 'root'
})
export class NamAttachmentService extends AbstractAttachmentService {
    public uploadFile(input: IUploadFileInput): Observable<unknown> {
        const url = this.getUploadServiceUrl(input);
        return this.httpClient
            .get<{ signedURL: string }>(url, {
                params: { contentType: input.file.file?.type ?? 'application/octet-stream' }
            })
            .pipe(switchMap((uploadUrl) => this.uploadFileToS3(uploadUrl, input.file)));
    }

    protected override uploadFileToS3(uploadUrl: { signedURL: string }, file: UploadFile): Observable<unknown> {
        const fileType = file.file?.type ?? 'application/octet-stream';
        const url = uploadUrl.signedURL;
        return this.httpClient
            .put(url, file.file, {
                headers: { 'Content-Type': fileType, 'If-None-Match': '*' }
            })
            .pipe(
                catchError((err) => {
                    this.snackbarService.openSnackbar({
                        type: 'Error',
                        title: "Couldn't upload file",
                        desc: file.name,
                        error: err
                    });
                    throw err;
                })
            );
    }

    public override downloadFile(input: IDownloadRequestAttachmentInput): Observable<any> {
        const url = this.getDownloadServiceUrl(input);

        return this.httpClient.get<{ signedURL: string }>(url).pipe(
            tap((downloadUrl) => {
                const link = document.createElement('a');
                link.setAttribute('target', '_blank');
                link.setAttribute('href', downloadUrl.signedURL);
                link.setAttribute('download', input.filePath);
                link.click();
                link.remove();
            }),
            catchError((err) => {
                this.snackbarService.openSnackbar({
                    type: 'Error',
                    title: "Couldn't download file",
                    desc: input.filePath,
                    error: err
                });
                throw err;
            })
        );
    }

    /**
     * Get the file service url based on the file entity
     */
    protected override getDownloadServiceUrl(input: IDownloadRequestAttachmentInput): string {
        switch (input.fileEntity) {
            case EFileEntity.Request:
                if (input.commentId !== undefined) {
                    throw new Error('Comment id should be undefined for request entity');
                }
                return `${environment.apiUrl}/requests/${input.requestId}/attachments/${encodeURIComponent(
                    input.filePath
                )}/links/download`;
            case EFileEntity.Comment:
                if (input.commentId === undefined) {
                    throw new Error('Comment id should be defined for comment entity');
                }
                return `${environment.apiUrl}/requests/${input.requestId}/comments/${
                    input.commentId
                }/attachments/${encodeURIComponent(input.filePath)}/links/download`;
            default:
                throw new Error('Invalid file entity');
        }
    }

    public deleteFile(input: IDeleteRequestFileInput): Observable<unknown> {
        const url = this.getDeleteServiceUrl(input);
        return this.httpClient.delete(url).pipe(
            catchError((err) => {
                this.snackbarService.openSnackbar({
                    type: 'Error',
                    title: "Couldn't delete file",
                    desc: input.filePath,
                    error: err
                });
                throw err;
            }),
            tap(() => {
                this.snackbarService.openSnackbar({
                    type: 'Success',
                    title: 'File deleted successfully',
                    desc: input.filePath
                });
            })
        );
    }

    public listFiles(input: IListFileInput): Observable<IS3File[]> {
        const url = this.getListServiceUrl(input);
        return this.httpClient.get<IS3File[]>(url).pipe(
            catchError((err) => {
                console.error(err);
                return of([]);
            })
        );
    }

    /**
     * Get the delete files service url based on the file entity
     */
    protected override getDeleteServiceUrl(input: IDeleteRequestFileInput): string {
        switch (input.fileEntity) {
            case EFileEntity.Request:
                if (input.commentId !== undefined) {
                    throw new Error('Comment id should be undefined for request entity');
                }
                return `${environment.apiUrl}/requests/${input.requestId}/attachments/${encodeURIComponent(
                    input.filePath
                )}`;
            case EFileEntity.Comment:
                if (input.commentId === undefined) {
                    throw new Error('Comment id should be defined for comment entity');
                }
                return `${environment.apiUrl}/requests/${input.requestId}/comments/${
                    input.commentId
                }/attachments/${encodeURIComponent(input.filePath)}`;
            default:
                throw new Error('Invalid file entity');
        }
    }

    /**
     * Get the list files service url based on the file entity
     */
    protected override getListServiceUrl(input: IListFileInput): string {
        const prefix = input.prefix || EMPTY_PATH_PREFIX;
        switch (input.fileEntity) {
            case EFileEntity.Request:
                if (input.commentId !== undefined) {
                    throw new Error('Comment id should be undefined for request entity');
                }
                return `${environment.apiUrl}/requests/${input.requestId}/attachments/${encodeURIComponent(prefix)}`;
            case EFileEntity.Comment:
                if (input.commentId === undefined) {
                    throw new Error('Comment id should be defined for comment entity');
                }
                return `${environment.apiUrl}/requests/${input.requestId}/comments/${
                    input.commentId
                }/attachments/${encodeURIComponent(prefix)}`;
            default:
                throw new Error('Invalid file entity');
        }
    }

    /**
     * Get the file service url based on the file entity
     */
    protected override getUploadServiceUrl(input: IUploadFileInput): string {
        switch (input.fileEntity) {
            case EFileEntity.Request:
                if (input.commentId !== undefined) {
                    throw new Error('Comment id should be undefined for request entity');
                }
                return `${environment.apiUrl}/requests/${input.requestId}/attachments/${encodeURIComponent(
                    `${input.file.name}`
                )}/links/upload`;
            case EFileEntity.Comment:
                if (input.commentId === undefined) {
                    throw new Error('Comment id should be defined for comment entity');
                }
                return `${environment.apiUrl}/requests/${input.requestId}/comments/${
                    input.commentId
                }/attachments/${encodeURIComponent(`${input.file.name}`)}/links/upload`;
            default:
                throw new Error('Invalid file entity');
        }
    }
}
